import React, { useState } from 'react';
import { Modal, Upload, Typography, Space, Divider, App } from 'antd';
import { InboxOutlined, FileTextOutlined, SendOutlined, DownloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { getTheme, primaryShades, greyScaleTokens } from '../../../theme/theme';
import { PrimaryButton, OutlineButton } from '../../Components/Buttons/Buttons';
import { uploadQuoteAndUpdateOrder } from '../../../services/api/catalogOrdersApi';

const { Title, Text } = Typography;
const { Dragger } = Upload;

const UploadQuoteModal = ({ visible, onCancel, order, onSuccess }) => {
    const [fileList, setFileList] = useState([]);
    const [sending, setSending] = useState(false);
    const theme = getTheme();
    const { message } = App.useApp();

    // Handle file upload props
    const uploadProps = {
        name: 'file',
        multiple: false,
        accept: '.pdf',
        fileList,
        beforeUpload: (file) => {
            // Validate file type
            const isPDF = file.type === 'application/pdf';
            if (!isPDF) {
                // Use setTimeout to ensure message is called in the next tick
                setTimeout(() => {
                    message.error('You can only upload PDF files!');
                }, 0);
                return false;
            }

            // Validate file size (max 10MB)
            const isLt10M = file.size / 1024 / 1024 < 10;
            if (!isLt10M) {
                // Use setTimeout to ensure message is called in the next tick
                setTimeout(() => {
                    message.error('File must be smaller than 10MB!');
                }, 0);
                return false;
            }

            // Add file to list
            setFileList([file]);
            return false; // Prevent auto upload
        },
        onRemove: () => {
            setFileList([]);
        },
    };

    // Handle sending the quote
    const handleSendQuote = async () => {
        if (fileList.length === 0) {
            message.warning('Please select a PDF file to upload');
            return;
        }

        if (!order?.id) {
            message.error('Order ID is missing');
            return;
        }

        try {
            setSending(true);

            // Upload the quote file and update the order
            const downloadURL = await uploadQuoteAndUpdateOrder(order.id, fileList[0]);

            message.success('Quote uploaded and sent successfully!');

            // Reset state
            setFileList([]);

            // Call success callback to refresh orders list
            if (onSuccess) {
                onSuccess();
            }

            // Close modal
            onCancel();

        } catch (error) {
            console.error('Error uploading quote:', error);
            message.error(`Failed to send quote: ${error.message}`);
        } finally {
            setSending(false);
        }
    };

    // Handle downloading the request document
    const handleDownloadRequest = async () => {
        if (!order?.excelReportUrl) {
            message.error('Request document is not available for download');
            return;
        }

        try {
            // Extract filename from URL more reliably
            let filename = `Orders-${order.id}-report.xlsx`; // Clean default name

            try {
                const url = new URL(order.excelReportUrl);
                const pathname = url.pathname;

                // Extract filename from pathname, handling Firebase Storage URLs
                if (pathname.includes('/')) {
                    const pathParts = pathname.split('/');
                    let extractedName = pathParts[pathParts.length - 1];

                    // Remove any URL encoding and clean up the filename
                    extractedName = decodeURIComponent(extractedName);

                    // The decoded name might still contain folder paths (e.g., "Orders/ZyQhQO7ss7MgLNUsTXKP/Orders-ZyQhQO7ss7MgLNUsTXKP-report.xlsx")
                    // Extract just the actual filename (last part after any remaining slashes)
                    if (extractedName.includes('/')) {
                        const fileParts = extractedName.split('/');
                        extractedName = fileParts[fileParts.length - 1];
                    }

                    // If the extracted name looks like a valid filename, use it
                    if (extractedName && extractedName.length > 0) {
                        // Add extension if missing
                        if (!extractedName.includes('.')) {
                            extractedName += '.xlsx';
                        }
                        filename = extractedName;
                    }
                }
            } catch (urlError) {
                console.warn('Could not parse URL for filename extraction:', urlError);
                // Keep the default filename
            }

            // Try to download via fetch to have more control over the filename
            try {
                const response = await fetch(order.excelReportUrl);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const blob = await response.blob();

                // Create blob URL and download
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                link.style.display = 'none';

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // Clean up blob URL
                window.URL.revokeObjectURL(blobUrl);

            } catch (fetchError) {
                console.warn('Fetch download failed, falling back to direct link:', fetchError);

                // Fallback to direct link method
                const link = document.createElement('a');
                link.href = order.excelReportUrl;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                link.download = filename;

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

            }
        } catch (error) {
            message.error('Failed to download request document');
        }
    };

    // Handle modal close
    const handleCancel = () => {
        if (sending) {
            message.warning('Please wait for the upload to complete');
            return;
        }

        setFileList([]);
        onCancel();
    };

    return (
        <Modal
            title="Upload Quote"
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={600}
            destroyOnHidden
            maskClosable={!sending}
            closable={!sending}
            centered
        >
            <div>
                {/* Order Info */}
                <div style={{
                    marginBottom: '24px',
                    padding: '16px',
                    background: greyScaleTokens.grey100,
                    borderRadius: '6px',
                    border: `1px solid ${theme.token.colorBorderSecondary}`
                }}>
                    <Text strong style={{ color: theme.token.colorText }}>Type: </Text>
                    <Text style={{ color: theme.token.colorTextSecondary }}>Order Assets</Text>
                    <br />
                    <Text strong style={{ color: theme.token.colorText }}>Status: </Text>
                    <Text style={{ color: theme.token.colorTextSecondary }}>Quote Requested</Text>
                </div>

                <Divider />

                {/* Download Request Section */}
                {order?.excelReportUrl && (
                    <div style={{ marginBottom: '24px' }}>
                        <Title level={4} style={{ color: theme.token.colorText }}>Request Document</Title>
                        <Text type="secondary" style={{
                            display: 'block',
                            marginBottom: '16px',
                            color: theme.token.colorTextSecondary
                        }}>
                            Download the original request document to review the items requested.
                        </Text>

                        <div style={{
                            padding: '16px',
                            border: `1px solid ${theme.token.colorBorder}`,
                            borderRadius: '6px',
                            background: greyScaleTokens.grey100,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div>
                                <Text strong style={{ color: primaryShades.primary600 }}>Request Document Available</Text>
                                <br />
                                <Text type="secondary" style={{ color: theme.token.colorTextSecondary }}>Click to download the original request file</Text>
                            </div>
                            <PrimaryButton
                                icon={<DownloadOutlined />}
                                onClick={handleDownloadRequest}
                            >
                                Download Request
                            </PrimaryButton>
                        </div>
                    </div>
                )}

                {!order?.excelReportUrl && (
                    <div style={{ marginBottom: '24px' }}>
                        <div style={{
                            padding: '16px',
                            border: `1px solid ${theme.token.colorBorderSecondary}`,
                            borderRadius: '6px',
                            background: greyScaleTokens.grey100
                        }}>
                            <Text type="secondary" style={{ color: theme.token.colorTextSecondary }}>
                                <FileTextOutlined style={{ marginRight: '8px', color: primaryShades.primary300 }} />
                                Request document is not available for this order.
                            </Text>
                        </div>
                    </div>
                )}

                <Divider />

                {/* Upload Section */}
                <div style={{ marginBottom: '24px' }}>
                    <Title level={4} style={{ color: theme.token.colorText }}>Upload Quote PDF</Title>
                    <Text type="secondary" style={{
                        display: 'block',
                        marginBottom: '16px',
                        color: theme.token.colorTextSecondary
                    }}>
                        Upload a PDF file containing the quote for this order. Maximum file size: 10MB.
                    </Text>

                    <Dragger
                        {...uploadProps}
                        style={{
                            padding: '1rem',
                            minHeight: '8rem',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            marginBottom: '16px'
                        }}
                        showUploadList={false}
                    >
                        <p className="ant-upload-drag-icon" style={{ fontSize: '2rem', margin: '0 0 0.5rem 0' }}>
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text" style={{ margin: '0 0 0.25rem 0' }}>
                            Click or drag PDF file to this area to upload
                        </p>
                        <p className="ant-upload-hint" style={{ margin: '0 0 0.5rem 0' }}>
                            Support for single PDF file upload. Maximum size: 10MB.
                        </p>

                        {/* Mostra il file caricato direttamente nell'area drag & drop */}
                        {fileList.length > 0 && (
                            <div style={{
                                marginTop: '0.75rem',
                                padding: '0.75rem',
                                backgroundColor: `${primaryShades.primary100}`,
                                border: `1px solid ${primaryShades.primary300}`,
                                borderRadius: '0.375rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                minWidth: '12rem',
                                width: 'fit-content',
                                margin: '0.75rem auto 0'
                            }}>
                                <div style={{ flexShrink: 0 }}>
                                    <FileTextOutlined style={{ fontSize: '1rem', color: primaryShades.primary500 }} />
                                </div>
                                <div style={{ flex: 1, textAlign: 'left' }}>
                                    <div style={{
                                        fontWeight: '600',
                                        fontSize: '0.875rem',
                                        color: primaryShades.primary700,
                                        marginBottom: '0.125rem'
                                    }}>
                                        {fileList[0].name}
                                    </div>
                                    <div style={{
                                        fontSize: '0.75rem',
                                        color: primaryShades.primary600
                                    }}>
                                        {(fileList[0].size / 1024 / 1024).toFixed(2)} MB
                                    </div>
                                </div>
                                <div
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setFileList([]);
                                    }}
                                    style={{
                                        cursor: 'pointer',
                                        padding: '4px',
                                        borderRadius: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'transparent',
                                        transition: 'background-color 0.2s',
                                        flexShrink: 0
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#fff1f0';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                    title="Remove file"
                                >
                                    <DeleteOutlined style={{
                                        fontSize: '16px',
                                        color: primaryShades.primary500
                                    }} />
                                </div>
                            </div>
                        )}
                    </Dragger>
                </div>

                <Divider />

                {/* Action Buttons */}
                <div style={{ textAlign: 'right' }}>
                    <Space>
                        <OutlineButton
                            onClick={handleCancel}
                            disabled={sending}
                        >
                            Cancel
                        </OutlineButton>
                        <PrimaryButton
                            icon={<SendOutlined />}
                            onClick={handleSendQuote}
                            loading={sending}
                            disabled={fileList.length === 0}
                        >
                            {sending ? 'Sending Quote...' : 'Send Quote'}
                        </PrimaryButton>
                    </Space>
                </div>
            </div>
        </Modal>
    );
};

export default UploadQuoteModal; 